import styles from "./PasswordStage.module.css"
import SubmitButton from "../../../../../components/Button/SubmitButton/SubmitButton";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import PasswordInput from "../../../../../components/Input/PasswordInput/PasswordInput";
import { createAccount } from "../../../../../services/authService";
import Loader from "../../../../../components/Loader/Loader";
import { log } from "../../../../../utils/logger";
/**
 * Password validation rules
 */
const validatePassword = (password: string) => {
    const rules = {
        minLength: password.length >= 6,
        hasNumber: /\d/.test(password),
        hasSymbol: /[\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/.test(password), // !"#$%&'()*+,-./:;<=>?@[\]^_{|}~`
        hasLower: /[a-z]/.test(password),
        hasUpper: /[A-Z]/.test(password)
    };
    
    const isValid = Object.values(rules).every(rule => rule);
    return { rules, isValid };
};

/**
* Props for the PasswordStage component.
*/ 
interface PasswordStageProps {
    otp: string | null;// Otp for account creation
    setLoading: (loading: boolean) => void,
    onAccountCreated: () => void; // On account create
}
/**
* Create account password stage
*
* @param props PasswordStageProps
* @param {otp: string} props.otp - Otp for account creation
* @param {setLoading (loading: boolean) => void} props.setLoading - Loading setter
* @param {onAccountCreate: ()=>void} - Account created setter
* @returns TSX.Element
*/
const PasswordStage = ({ otp, setLoading, onAccountCreated }: PasswordStageProps) => {
    const { t } = useTranslation(); // Translation
    const nav = useNavigate() // Navigate

    // States for protected inputs
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showValidation, setShowValidation] = useState(false);
    const [error, setError] = useState<string>("");

    const { rules, isValid } = validatePassword(password);
    const passwordsMatch = password === confirmPassword && password !== "";

    const handlePasswordChange = (newPassword: string) => {
        setPassword(newPassword);
        setError("");
        if (newPassword.length > 0) {
            setShowValidation(true);
        }
    };


    const handleSubmit = async () => {
        if (isValid && passwordsMatch && otp) {
            setLoading(true);
            try {
                const result = await createAccount( otp, password );
                if(result.success){
                    log("Account created successfully");
                    onAccountCreated(); // Created callback
                }else {
                    //log(result)
                    log("Account creation failed:", result.error);
                    if(result.error.code && result.error.code === "auth/email-already-in-use" ){
                        setError("exist")
                    }else{
                        setError("fail")
                    }
                    // Show error message to user
                }
            } catch (error) {
                log("Account creation failed:", error);
                setError("fail");
            } finally {
                setLoading(false);
            }
        }
    };

    const canSubmit = isValid && passwordsMatch && otp; 

    return(
        <>
            <div className={styles.FormBlock}>
                <div className={styles.FormSubtitle}>
                    <p className={styles.Title}>{t("create_account.password_title")}</p>
                    {/*<p>{email}</p>*/}
                </div>
                <form action="" className={styles.Form} onSubmit={(e)=>{
                e.preventDefault();
                handleSubmit();
                    }}>
                    <div className={styles.InputCapsule}>
                        
                        <PasswordInput  text={t("forgot_password.text")} password={password} onChange={handlePasswordChange}/>
                        {/* Password validation feedback */}
                        {showValidation && (
                            <div className={styles.ValidationRules}>
                                <ValidationRule 
                                    isValid={rules.minLength} 
                                    text={t("create_account.validation.characters")}
                                />
                                <ValidationRule 
                                    isValid={rules.hasNumber} 
                                    text={t("create_account.validation.number")}
                                />
                                <ValidationRule 
                                    isValid={rules.hasSymbol} 
                                    text={t("create_account.validation.symbol")}
                                />
                                <ValidationRule 
                                    isValid={rules.hasLower} 
                                    text={t("create_account.validation.lower")}
                                />
                                <ValidationRule 
                                    isValid={rules.hasUpper} 
                                    text={t("create_account.validation.upper")}
                                />
                                
                            </div>
                        )}
                        
                        <PasswordInput text={t("forgot_password.text_again")} password={confirmPassword} onChange={setConfirmPassword}/>
                        {/* Password match feedback */}
                        {confirmPassword && !passwordsMatch && (
                            <div className={styles.ErrorMessage}>
                                {t("create_account.validation.match")}
                            </div>
                        )}
                        {/*Display error if it happens */}
                        {error !== "" && (
                            <div className={styles.ErrorMessage}>
                                {error === "exist" ? (
                                    t("create_account.fail_existing")
                                ):(
                                    t("create_account.fail")
                                )}
                            </div>
                        )}
                    </div>
                    <SubmitButton text={t("forgot_password.submit")} onClick={handleSubmit} disabled={!canSubmit}/>
                </form>

        </div>
        </>
    )
};
/**
 * Individual validation rule component
 */
const ValidationRule = ({ isValid, text }: { isValid: boolean; text: string }) => (
    <div className={`${styles.ValidationRule} ${isValid ? styles.Valid : styles.Invalid}`}>
        <span className={styles.ValidationIcon}>
            {isValid ? "✓" : "✗"}
        </span>
        {text}
    </div>
);

export default PasswordStage;

/*
Oldversion


import styles from "./ResetStage.module.css"
import SubmitButton from "../../../../../components/Button/SubmitButton/SubmitButton";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import PasswordInput from "../../../../../components/Input/PasswordInput/PasswordInput";
interface ResetStageProps {
    onBack?: () => void;
}

const ResetStage = ({ onBack }: ResetStageProps) => {
    const { t } = useTranslation(); // Translation
    const nav = useNavigate() // Navigate

    // States for protected inputs
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    return(
        <>
            <div className={styles.FormBlock}>
                <form action="" className={styles.Form} onSubmit={(e)=>{
                e.preventDefault();
                    }}>
                    <div className={styles.InputCapsule}>
                        <PasswordInput password={password} onChange={setPassword}/>
                        <PasswordInput text="Heslo znovu" password={confirmPassword} onChange={setConfirmPassword}/>
                    </div>
                    <SubmitButton text="Potvrdit"/>
                </form>
            <div className={styles.FormSubtitle}><p>{t("forgot_password.reset_sent")}</p></div>
            <p className={styles.Hint}>{t("forgot_password.go_back")}</p>
            <SubmitButton text={t("buttons.back")} onClick={onBack}/>
        </div>
        </>
    )
};

export default ResetStage;





*/
